// XSL visual (catalog ID "xml", className "Insightxsl").
//
// The result set is serialized to XML and transformed by the author's XSL
// stylesheet (the `xslTemplate` option) using the browser's XSLTProcessor. The
// transformed output is what the user sees.
//
// Until this existed the type was aliased to the plain HTML renderer, which
// only looked for a column named like /html|content|body|template/ and dumped
// it — the XML was never built and no transform ever ran, so an XSL visual
// could not produce its formatted output. With no stylesheet configured we
// still fall back to that HTML behaviour so an existing visual keeps rendering.

import { useMemo } from 'react'
import { mapVisualData } from './dataUtils'
import { HtmlVisual, Notice } from './nativeRenderers'
import type { NativeRendererProps } from './nativeRenderers'
import type { VisualOption } from './types'

/** XML 1.0 text escape for element content and attribute values. */
function escapeXml(value: unknown): string {
  if (value === null || value === undefined) return ''
  return String(value)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&apos;')
}

/** A column NAME is not guaranteed to be a legal XML element name (it can start
 *  with a digit, or carry spaces/punctuation from a SELECT alias). Normalise to
 *  a safe tag and keep the original in a `name` attribute so the stylesheet can
 *  still address it. */
function safeTag(name: string): string {
  const cleaned = name.replace(/[^A-Za-z0-9_.-]/g, '_')
  return /^[A-Za-z_]/.test(cleaned) ? cleaned : `_${cleaned}`
}

/**
 * `<rows><row><COLUMN name="COLUMN">value</COLUMN>…</row>…</rows>`
 *
 * Exported so the shape is testable and so the designer can show authors the
 * document their stylesheet will match against.
 */
export function rowsToXml(
  rows: Array<Record<string, unknown>>,
  columns: string[],
): string {
  const body = rows.map((row) => {
    const cells = columns.map((c) => {
      const tag = safeTag(c)
      return `    <${tag} name="${escapeXml(c)}">${escapeXml(row[c])}</${tag}>`
    })
    return `  <row>\n${cells.join('\n')}\n  </row>`
  })
  return `<?xml version="1.0" encoding="UTF-8"?>\n<rows>\n${body.join('\n')}\n</rows>`
}

/** The chosen value, else the catalog default — the same precedence the other
 *  native renderers use (see CalendarVisual.readOption). The designer writes
 *  the authored text into `defaultValue`, but hosts that carry a separate
 *  `value` must not have their stylesheet silently ignored. */
function optionValue(options: VisualOption[] | undefined, name: string): string {
  const hit = (options || []).find(
    (o) => String(o.optionName || '').toLowerCase() === name.toLowerCase(),
  )
  if (!hit) return ''
  const v = hit.value ?? hit.defaultValue
  return v == null ? '' : String(v)
}

export function XslVisual(props: NativeRendererProps) {
  const { jsonData, visualLayout, optionsArr } = props
  const options = optionsArr ?? visualLayout?.options
  const stylesheet = optionValue(options, 'xslTemplate').trim()

  const md = useMemo(
    () => mapVisualData(jsonData, visualLayout),
    [jsonData, visualLayout],
  )

  const result = useMemo(() => {
    if (!stylesheet) return null
    const columns = Object.keys(md.schema).filter((k) => k !== 'id')
    const xml = rowsToXml(md.rows, columns)
    try {
      const parser = new DOMParser()
      const xslDoc = parser.parseFromString(stylesheet, 'application/xml')
      // A malformed stylesheet parses into a document whose root is
      // <parsererror> rather than throwing — check for it explicitly.
      const xslError = xslDoc.querySelector('parsererror')
      if (xslError) {
        return { error: `XSL stylesheet is not valid XML: ${xslError.textContent?.trim() || 'parse error'}` }
      }
      const xmlDoc = parser.parseFromString(xml, 'application/xml')
      const processor = new XSLTProcessor()
      processor.importStylesheet(xslDoc)
      const fragment = processor.transformToFragment(xmlDoc, document)
      if (!fragment) return { error: 'The XSL transform produced no output.' }
      const holder = document.createElement('div')
      holder.appendChild(fragment)
      return { html: holder.innerHTML }
    } catch (err) {
      return { error: `XSL transform failed: ${(err as Error).message}` }
    }
  }, [stylesheet, md])

  // No stylesheet configured → keep the previous HTML-content behaviour.
  if (!stylesheet) return <HtmlVisual {...props} />
  if (!md.rows.length) return <Notice text="No data to transform." />
  if (result?.error) return <Notice text={result.error} />

  return (
    <div
      style={{ padding: 12, overflow: 'auto', height: '100%', boxSizing: 'border-box' }}
      // eslint-disable-next-line react/no-danger -- the stylesheet is authored
      // in the designer by the project's own author, same trust level as the
      // Html visual's content column.
      dangerouslySetInnerHTML={{ __html: result?.html ?? '' }}
    />
  )
}
